import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { RAGDOLL_CONSTRAINTS, RAGDOLL_JOINT_MASS } from './config';

// Pre-allocated temporaries — avoid per-frame heap pressure
const _parentInv = new THREE.Matrix4();
const _physMat   = new THREE.Matrix4();
const _localMat  = new THREE.Matrix4();
const _physPos   = new THREE.Vector3();
const _physQuat  = new THREE.Quaternion();
const _lPos      = new THREE.Vector3();
const _lQuat     = new THREE.Quaternion();
const _lScale    = new THREE.Vector3();
const _one       = new THREE.Vector3(1, 1, 1);

// ─────────────────────────────────────────────────────────────
// activateRagdoll
// ─────────────────────────────────────────────────────────────
export const activateRagdoll = (inst, world) => {
    if (inst.isRagdollActive) return;

    // Freeze the main capsule so it doesn't fight the ragdoll
    inst.mainBody.type = CANNON.Body.STATIC;
    inst.mainBody.velocity.set(0, 0, 0);
    inst.mainBody.angularVelocity.set(0, 0, 0);

    // Switch every joint body to DYNAMIC
    for (const rb of inst.ragdollBodies) {

        // 👇 【新增代码】缓存骨骼初始的局部位置和旋转（只在第一次记录，保留原始 Rest Pose）
        if (!rb.restPosition) {
            rb.restPosition = rb.bone.position.clone();
            rb.restQuaternion = rb.bone.quaternion.clone();
        }

        rb.body.type = CANNON.Body.DYNAMIC;
        rb.body.mass = RAGDOLL_JOINT_MASS;
        rb.body.updateMassProperties();
        rb.body.linearDamping  = 0.4;
        rb.body.angularDamping = 0.6;
        rb.body.velocity.set(0, 0, 0);
        rb.body.angularVelocity.set(0, 0, 0);
        rb.body.wakeUp();
    }

    // Wire adjacent joints together with PointToPointConstraints.
    // pivotA = child world pos − parent world pos (offset in parent's rest frame)
    // pivotB = (0,0,0)  (child connects at its own centre)
    const constraints = [];
    for (const [pIdx, cIdx] of RAGDOLL_CONSTRAINTS) {
        const parentBody = inst.ragdollBodies[pIdx]?.body;
        const childBody  = inst.ragdollBodies[cIdx]?.body;
        if (!parentBody || !childBody) continue;

        const PA = parentBody.position;
        const PB = childBody.position;
        const c = new CANNON.PointToPointConstraint(
            parentBody,
            new CANNON.Vec3(PB.x - PA.x, PB.y - PA.y, PB.z - PA.z),
            childBody,
            new CANNON.Vec3(0, 0, 0)
        );
        world.addConstraint(c);
        constraints.push(c);
    }

    inst.ragdollConstraints  = constraints;
    // Record model → Hips world offset so we can move the model with the ragdoll
    inst.ragdollModelOffset  = null; // will be filled on first applyPhysicsToSkeleton call
    inst.isRagdollActive     = true;
};

// ─────────────────────────────────────────────────────────────
// deactivateRagdoll
// ─────────────────────────────────────────────────────────────
export const deactivateRagdoll = (inst, world) => {
    if (!inst.isRagdollActive) return;

    // Before restoring, park mainBody near where the ragdoll ended up
    const hipsBody = inst.ragdollBodies[0]?.body;
    if (hipsBody && inst.ragdollModelOffset) {
        inst.mainBody.position.set(
            hipsBody.position.x + inst.ragdollModelOffset.x,
            hipsBody.position.y + inst.ragdollModelOffset.y,
            hipsBody.position.z + inst.ragdollModelOffset.z
        );
    }

    // Remove all constraints
    for (const c of (inst.ragdollConstraints || [])) {
        world.removeConstraint(c);
    }
    inst.ragdollConstraints = [];

    // Return joint bodies to kinematic (animation-driven)
    for (const rb of inst.ragdollBodies) {

        // 👇 【新增代码】恢复骨骼的局部坐标，修复关节脱臼和扭曲的问题
        if (rb.restPosition) {
            rb.bone.position.copy(rb.restPosition);
            // 顺便恢复旋转，让 AnimationMixer 有一个干净的起始状态
            rb.bone.quaternion.copy(rb.restQuaternion);
            rb.bone.updateMatrix();
        }
        
        rb.body.type = CANNON.Body.KINEMATIC;
        rb.body.mass = 0;
        rb.body.updateMassProperties();
        rb.body.velocity.set(0, 0, 0);
        rb.body.angularVelocity.set(0, 0, 0);
    }

    // Restore main capsule body
    inst.mainBody.type = CANNON.Body.DYNAMIC;
    inst.mainBody.velocity.set(0, 0, 0);

    // Sync model position from the updated mainBody so animation picks up
    // from the ragdoll's landing spot
    inst.model.position.set(
        inst.mainBody.position.x,
        inst.mainBody.position.y,
        inst.mainBody.position.z
    );

    inst.ragdollModelOffset = null;
    inst.isRagdollActive    = false;
};

// ─────────────────────────────────────────────────────────────
// applyPhysicsToSkeleton
// Called each frame while isRagdollActive.
//
// Strategy:
//   1. Keep model.position tracking the Hips body (preserving the
//      rest-pose model→Hips offset so no coordinate shift).
//   2. For each ragdoll joint, derive the bone's LOCAL transform from
//      the physics body's WORLD transform:
//        boneLocal = parent.matrixWorld⁻¹ × physicsWorldMatrix
//      Bones are processed root→leaf (RAGDOLL_JOINTS order) and each
//      bone's matrixWorld is propagated before the next iteration.
// ─────────────────────────────────────────────────────────────
export const applyPhysicsToSkeleton = (inst) => {
    const hipsBody = inst.ragdollBodies[0]?.body;
    if (!hipsBody) return;

    // Lazily record the stable model↔hips offset the very first frame
    if (!inst.ragdollModelOffset) {
        inst.ragdollModelOffset = new THREE.Vector3(
            inst.model.position.x - hipsBody.position.x,
            inst.model.position.y - hipsBody.position.y,
            inst.model.position.z - hipsBody.position.z
        );
    }

    // Move model root to stay anchored relative to the Hips body
    inst.model.position.set(
        hipsBody.position.x + inst.ragdollModelOffset.x,
        hipsBody.position.y + inst.ragdollModelOffset.y,
        hipsBody.position.z + inst.ragdollModelOffset.z
    );
    // Recompute model's own matrixWorld (don't recurse into children yet)
    inst.model.updateMatrix();
    inst.model.matrixWorld.multiplyMatrices(
        inst.model.parent ? inst.model.parent.matrixWorld : new THREE.Matrix4(),
        inst.model.matrix
    );

    // Set each bone's local transform from its physics body's world transform.
    // updateWorldMatrix(true, false) on the parent walks up to the scene root
    // recomputing each ancestor's matrixWorld from their stored local matrices
    // (which we've already updated for earlier bones in this loop).
    for (const { bone, body } of inst.ragdollBodies) {
        if (!bone.parent) continue;

        // Propagate ancestor chain so parent.matrixWorld is fresh
        bone.parent.updateWorldMatrix(true, false);

        _physPos.set(body.position.x, body.position.y, body.position.z);
        _physQuat.set(
            body.quaternion.x, body.quaternion.y,
            body.quaternion.z, body.quaternion.w
        );
        _physMat.compose(_physPos, _physQuat, _one);

        _parentInv.copy(bone.parent.matrixWorld).invert();
        _localMat.multiplyMatrices(_parentInv, _physMat);
        _localMat.decompose(_lPos, _lQuat, _lScale);

        bone.position.copy(_lPos);
        bone.quaternion.copy(_lQuat);
        bone.updateMatrix();
        // Directly set this bone's world matrix so the next bone in the
        // loop sees the updated ancestor chain without a full tree walk
        bone.matrixWorld.multiplyMatrices(bone.parent.matrixWorld, bone.matrix);
    }
};

// ─────────────────────────────────────────────────────────────
// Drag constraint helpers
// ─────────────────────────────────────────────────────────────
export const createDragConstraint = (world, inst, hitPoint) => {
    // Find the ragdoll joint closest to the click point
    let closestBody = null;
    let closestDist = Infinity;
    for (const rb of inst.ragdollBodies) {
        const bp = rb.body.position;
        const d  = Math.hypot(
            bp.x - hitPoint.x,
            bp.y - hitPoint.y,
            bp.z - hitPoint.z
        );
        if (d < closestDist) { closestDist = d; closestBody = rb.body; }
    }
    if (!closestBody) return null;

    // KINEMATIC cursor body that follows the mouse in world space
    const cursorBody = new CANNON.Body({ mass: 0, type: CANNON.Body.KINEMATIC });
    cursorBody.position.set(hitPoint.x, hitPoint.y, hitPoint.z);
    world.addBody(cursorBody);

    // Pivot on the grabbed body at the hit point (in body-local space)
    const pb = closestBody.position;
    const grabConstraint = new CANNON.PointToPointConstraint(
        closestBody,
        new CANNON.Vec3(hitPoint.x - pb.x, hitPoint.y - pb.y, hitPoint.z - pb.z),
        cursorBody,
        new CANNON.Vec3(0, 0, 0)
    );
    world.addConstraint(grabConstraint);

    return { cursorBody, grabConstraint, grabbedBody: closestBody };
};

export const updateDragConstraint = (dragData, worldPoint) => {
    if (!dragData?.cursorBody) return;
    dragData.cursorBody.position.set(worldPoint.x, worldPoint.y, worldPoint.z);
    dragData.cursorBody.velocity.set(0, 0, 0);
    dragData.cursorBody.wakeUp();
    dragData.grabbedBody?.wakeUp();
};

export const removeDragConstraint = (world, dragData) => {
    if (!dragData) return;
    if (dragData.grabConstraint) world.removeConstraint(dragData.grabConstraint);
    if (dragData.cursorBody)     world.removeBody(dragData.cursorBody);
};
